import { TextField as MUITextField, Box } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { gray, error } from "configs/colors";
import { lineHeight, rem } from "utils";

import { NumberFieldProps } from "./NumberField.types";
import { generateFormFieldStyles } from "../helpers";

export const Container = styled(Box)(() => ({
  display: "block",

  // Input Label
  "& > .MuiFormLabel-root": {
    marginBottom: rem(6),
  },

  // Helper text
  ".MuiFormHelperText-root": {
    marginLeft: 0,
    marginRight: 0,
    marginTop: rem(6),
    "&:not(.Mui-error), &.Mui-disabled": {
      color: gray[600],
    },
  },
}));

export const InputWrapper = styled(Box)<{
  color: NumberFieldProps["color"];
  fullWidth: boolean;
}>(({ theme, fullWidth, color }) => {
  const styles = generateFormFieldStyles({ theme, color });

  return {
    display: fullWidth ? "flex" : "inline-flex",
    boxShadow: styles.normal.boxShadow,
    borderRadius: theme.shape.borderRadius,
  };
});

export const AddonWrapper = styled(Box)<{
  placement: "before" | "after";
  disabled?: boolean;
}>(({ theme, placement, disabled }) => {
  const isBefore = placement === "before";
  const isAfter = placement === "after";
  const styles = generateFormFieldStyles({ theme });

  return {
    flex: "0 0 auto",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    padding: `${rem(7)} ${rem(12)}`,
    fontSize: rem(12),
    lineHeight: lineHeight(20),
    backgroundColor: disabled
      ? styles.disabled.backgroundColor
      : styles.normal.backgroundColor,
    border: `1px solid ${
      disabled ? styles.disabled.borderColor : styles.normal.borderColor
    }`,
    borderTopLeftRadius: isBefore ? theme.shape.borderRadius : 0,
    borderBottomLeftRadius: isBefore ? theme.shape.borderRadius : 0,
    borderTopRightRadius: isAfter ? theme.shape.borderRadius : 0,
    borderBottomRightRadius: isAfter ? theme.shape.borderRadius : 0,
    ...(isBefore ? { borderRight: 0 } : {}),
    ...(isAfter ? { borderLeft: 0 } : {}),
    ...(disabled ? { pointerEvents: "none" } : {}),
  };
});

export const Input = styled(MUITextField)<{
  textAlign?: "left" | "right";
  noBorder?: boolean;
  withAddonBefore?: boolean;
  withAddonAfter?: boolean;
}>(
  ({
    theme,
    color,
    textAlign,
    withAddonAfter,
    withAddonBefore,
    noBorder,
    InputProps,
  }) => {
    const styles = generateFormFieldStyles({
      theme,
      color,
    });

    return {
      marginTop: 0,
      zIndex: 2,

      // Input root
      ".MuiInputBase-root": {
        backgroundColor: styles.normal.backgroundColor,
        height: "100%",
        borderRadius: noBorder ? 0 : theme.shape.borderRadius,
        ...(withAddonBefore
          ? {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }
          : {}),
        ...(withAddonAfter
          ? {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }
          : {}),
        "&.MuiOutlinedInput-root": {
          ...(InputProps?.startAdornment ? { paddingLeft: rem(12) } : {}),
          ...(InputProps?.endAdornment ? { paddingRight: rem(12) } : {}),
        },
      },

      // Input element
      ".MuiInputBase-input": {
        padding: `${rem(8)} ${rem(12)}`,
        height: rem(20),
        "& #spin": {
          display: "none",
        },
      },

      // Fieldset
      ".MuiOutlinedInput-notchedOutline": {
        borderWidth: "1px !important",
        borderColor: styles.normal.borderColor,
        transition: theme.transitions.create([
          "border-color",
          "background-color",
          "box-shadow",
        ]),
      },

      "svg + .MuiOutlinedInput-notchedOutline": {
        borderWidth: "0 !important",
        borderColor: "unset",
        boxShadow: "unset",
      },

      ".MuiInputAdornment-root:has(+.rc-input-number)": {
        paddingLeft: rem(8),
      },

      ".MuiInputBase-root:has(.MuiSelect-select)": {
        boxShadow: "unset !important",
      },

      ".MuiBox-root:has(.MuiSelect-select) + .rc-input-number": {
        paddingLeft: "0",
      },
      ".rc-input-number:has(+ .MuiBox-root)": {
        paddingRight: "0",
      },
      ".rc-input-number-out-of-range input": {
        color: error[600],
      },
      ".rc-input-number input": {
        textAlign: `${textAlign ?? "left"}`,
        padding: 0,
      },
      // Custom color placeholder
      ".rc-input-number input::placeholder": {
        color: alpha(theme.palette.text.primary, 0.42),
      },

      // Disable
      ".Mui-disabled": {
        "&.MuiInputBase-root": {
          backgroundColor: styles.disabled.backgroundColor,
        },

        "&.MuiInputBase-input": {
          "-webkit-text-fill-color": styles.disabled.color,
        },

        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: `${styles.disabled.borderColor} !important`,
        },
      },

      // Without error
      "&:not(.Mui-error)": {
        // Hover
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: styles.normal.hover.borderColor,
        },
        // Focus
        ".Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: styles.normal.focus.borderColor,
          boxShadow: styles.normal.focus.boxShadow,
        },
        ".Mui-focused svg + .MuiOutlinedInput-notchedOutline": {
          boxShadow: "unset",
        },
      },

      // Error
      ".Mui-error": {
        // Hover
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: styles.error.hover.borderColor,
          boxShadow: styles.error.focus.boxShadow,
        },
        // Focus
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: styles.error.focus.borderColor,
          boxShadow: styles.error.focus.boxShadow,
        },
        "&.Mui-focused svg + .MuiOutlinedInput-notchedOutline": {
          boxShadow: "unset",
        },
        "&:hover svg + .MuiOutlinedInput-notchedOutline": {
          boxShadow: "unset",
        },
      },
      ".rc-input-number-input": {
        border: 0,
        outline: "none",
        width: "100%",
        height: "100%",
        font: "inherit",
      },
    };
  }
);
